import { ErrorSeverity } from '../shared/types';
import StatusCode from 'status-code-enum';
import { Timer } from '@lib/shared';
import { sources } from '../api/sources';
import { IProcessPaymentWithManifestRequest, TransactionStatus } from '@ace-clublabs/payment-types';
import { IPayWithStoredPaymentRequest } from '../types';
import { LogCannon } from '@ace-clublabs/log-cannon';

const previousTransactionTimedOut = 'previous-transaction-timed-out';

export class ProcessPaymentHelper {
  static waitForCompleteTransaction = async (id: string) => {
    const errorStatuses = [
      StatusCode.ClientErrorNotFound,
      StatusCode.ServerErrorInternal,
      StatusCode.ServerErrorBadGateway,
    ];
    // GET transaction returns exactly these codes for a completed transaction; anything else (202, or an
    // unhandled error status like 503/504 from axios) means the result isn't known yet - keep polling.
    const terminalStatuses = [
      StatusCode.SuccessOK,
      StatusCode.SuccessMultiStatus,
      StatusCode.ClientErrorBadRequest,
      StatusCode.ClientErrorPaymentRequired,
    ];
    const MAX = 60;
    let i = 0;
    while (i < MAX) {
      const { statusCode, transactionStatus, message, code } = (await sources.getTransactionStatus(id)) || {};
      LogCannon.blast(`Transaction status polling attempts`, {
        pollingAttempted: i,
        transactionId: id,
        statusCode,
        transactionStatus,
        message,
        code,
      });
      if (errorStatuses.includes(statusCode)) {
        LogCannon.error(`Transaction status polling received error status code. Transaction ID: ${id}`, {
          statusCode,
          pollingAttempted: i,
        });
        return Promise.reject({ type: ErrorSeverity.SOFT, message: 'payment_status_unknown' });
      }
      if (terminalStatuses.includes(statusCode)) {
        return transactionStatus === TransactionStatus.Failed
          ? Promise.reject({ transactionStatus, type: ErrorSeverity.SOFT, message, code })
          : Promise.resolve({ transactionStatus });
      }
      await Timer.sleep(1000);
      i++;
    }
    LogCannon.error(`Polling timed out. No status received within the specified time limit. Transaction ID: ${id}`);
    return Promise.reject({ type: ErrorSeverity.SOFT, message: 'payment_status_unknown' });
  };

  static processPayment = async (
    request: IProcessPaymentWithManifestRequest
  ): Promise<{ transactionId: string; status?: TransactionStatus }> => {
    const { transactionId, transactionProcessStatus } = await sources.processPayment(request);
    let status;

    if (transactionProcessStatus === previousTransactionTimedOut) {
      LogCannon.error(`Transaction timed out during processPayment flow. Transaction ID: ${transactionId}`);
      throw { type: ErrorSeverity.HARD };
    }
    if (transactionProcessStatus !== previousTransactionTimedOut) {
      const result = await ProcessPaymentHelper.waitForCompleteTransaction(transactionId);
      status = result?.transactionStatus;
    }

    return Promise.resolve({ status, transactionId });
  };

  static payWithStoredPayment = async (
    request: IPayWithStoredPaymentRequest
  ): Promise<{ transactionId: string; status?: TransactionStatus }> => {
    const { transactionId, transactionProcessStatus } = await sources.payWithStoredPayment(request);
    let status;

    if (transactionProcessStatus === previousTransactionTimedOut) {
      LogCannon.error(`Transaction timed out during payWithStoredPayment flow. Transaction ID: ${transactionId}`);
      throw { type: ErrorSeverity.HARD };
    }

    if (transactionProcessStatus !== previousTransactionTimedOut) {
      const { transactionStatus } = (await ProcessPaymentHelper.waitForCompleteTransaction(transactionId))!;
      status = transactionStatus;
    }

    return Promise.resolve({ status, transactionId });
  };

  //used for async digital wallet processing
  static startDigitalWalletPayment = async (request: IProcessPaymentWithManifestRequest): Promise<string> => {
    const { transactionId, transactionProcessStatus } = await sources.processPayment(request);

    if (transactionProcessStatus === previousTransactionTimedOut) {
      LogCannon.error(`Transaction timed out during startDigitalWalletPayment flow. Transaction ID: ${transactionId}`);
      throw { type: ErrorSeverity.HARD };
    }

    return transactionId;
  };
}
